import { InMemoryCacheConfig } from '@apollo/client';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';

import { apolloConfig } from '@willow/graphql-iso/src/portal';
import { Auth0Domain, isDefaultPortalHostnameStatic, isFindPortalPath } from '@willow/shared-iso';
import { AuthorizedProvider, getAppContext, InitLoader, initSentry, UniversalLoader } from '@willow/shared-web';
import { PORTAL_LOGIN_PATH, PORTAL_SET_PASSWORD_PATH } from '@willow/types-iso';

import { App } from './App';
import { FORGOT_PASSWORD_PATH, isAuthPath } from './auth/authRoutes';
import { hardNavigate } from './auth/hardNavigate';
import { PortalAuthProvider } from './auth/PortalAuthProvider';
import { useLoginConfig } from './auth/useLoginConfig';
import { FindPortalApp } from './find-portal/FindPortalApp';
import { ForgotPasswordPage } from './pages/forgot-password/ForgotPasswordPage';
import { LoginPage } from './pages/login/LoginPage';
import { SetPasswordPage } from './pages/set-password/SetPasswordPage';
import reportWebVitals from './reportWebVitals';

import './index.scss';

const APP_CONTEXT = getAppContext();
const AUTH0_DOMAIN = Auth0Domain.getCustomDomainForPortal(APP_CONTEXT);
const AUTH0_AUDIENCE = `https://${Auth0Domain.getForPortal(APP_CONTEXT)}/api/v2/`;
// Relative in every environment. Local dev reaches the app server through the Vite proxy, which
// keeps the SPA and the API on one origin so the same-origin check can be exercised.
const GRAPHQL_ENDPOINT = '/api/portal/graphql';
const PORTAL_CACHE_CONFIG: InMemoryCacheConfig = {
  typePolicies: {
    PortalLoanSnapshot: {
      keyFields: ['loanId'], // Declare index field so that Apollo merges responses correctly
    },
    Borrower: {
      keyFields: ['id', 'companyId'],
    },
  },
  possibleTypes: apolloConfig.possibleTypes,
};

const isSessionStorageAvailable = () => {
  try {
    const testKey = '__test__';
    window.sessionStorage.setItem(testKey, '1');
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

// The retry link can surface the same 401 several times before the document unloads, so the
// first redirect wins and the rest are dropped.
let redirectingToLogin = false;

const redirectToLogin = () => {
  const { pathname, search, hash } = window.location;
  if (redirectingToLogin || isAuthPath(pathname)) return;
  redirectingToLogin = true;
  hardNavigate(`${PORTAL_LOGIN_PATH}?returnTo=${encodeURIComponent(`${pathname}${search}${hash}`)}`);
};

export const PortalApp = () => {
  // This has to stay above the cookie and session-storage guards below. A hook that sits after an
  // early return is skipped on the renders that take that return, which changes the hook order
  // React sees between renders and corrupts hook state.
  useEffect(() => {
    // If you want to start measuring performance in your app, pass a function
    // to log results (for example: reportWebVitals(console.log))
    // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
    reportWebVitals();
    if (APP_CONTEXT === 'production') {
      initSentry();
    }
  }, []);
  const loginConfig = useLoginConfig();

  // Before the cookie and session-storage guards: the router page needs neither, and a
  // borrower without them should still be routed to the Portal where they do apply.
  if (
    isDefaultPortalHostnameStatic(window.location.hostname, APP_CONTEXT) &&
    isFindPortalPath(window.location.pathname)
  ) {
    return <FindPortalApp />;
  }

  if (!navigator.cookieEnabled) {
    return <>Please enable cookies to view this site</>;
  }
  if (!isSessionStorageAvailable()) {
    return (
      <>
        To use this site, please enable session storage in your browser settings. Session storage is required to
        maintain your login state and preferences. If you're using private/incognito mode, try switching to a regular
        browser window or adjust your browser settings to allow session storage.
      </>
    );
  }

  const willowAuth =
    loginConfig.status === 'ready' && loginConfig.config.willowAuthEnabled ? loginConfig.config : undefined;

  const renderContents = (): JSX.Element => {
    // An empty child keeps the InitLoader up until a page reports it is ready.
    if (loginConfig.status === 'loading') return <></>;

    if (willowAuth) {
      return (
        <Switch>
          <Route path={PORTAL_LOGIN_PATH}>
            <LoginPage config={willowAuth} />
          </Route>
          <Route path={FORGOT_PASSWORD_PATH}>
            <ForgotPasswordPage config={willowAuth} />
          </Route>
          <Route path={PORTAL_SET_PASSWORD_PATH}>
            <SetPasswordPage config={willowAuth} />
          </Route>
          <Route>
            <AuthorizedProvider
              mode="cookie"
              graphqlEndpoint={GRAPHQL_ENDPOINT}
              apolloCacheConfig={PORTAL_CACHE_CONFIG}
              onUnauthenticated={redirectToLogin}
            >
              <PortalAuthProvider mode="cookie">
                <App appContextEnv={APP_CONTEXT} />
              </PortalAuthProvider>
            </AuthorizedProvider>
          </Route>
        </Switch>
      );
    }

    return (
      <AuthorizedProvider
        auth0Domain={AUTH0_DOMAIN}
        auth0ClientId={Auth0Domain.getPortalClientId(APP_CONTEXT)}
        auth0Audience={AUTH0_AUDIENCE}
        graphqlEndpoint={GRAPHQL_ENDPOINT}
        apolloCacheConfig={PORTAL_CACHE_CONFIG}
      >
        <PortalAuthProvider mode="auth0">
          <App appContextEnv={APP_CONTEXT} />
        </PortalAuthProvider>
      </AuthorizedProvider>
    );
  };

  return (
    <Router>
      <HelmetProvider>
        <QueryParamProvider adapter={ReactRouter5Adapter}>
          {/* eslint-disable-next-line react/no-unstable-nested-components -- TODO: pre-existing, surfaced by removing the NamedMemo wrapper; fix in follow-up */}
          <UniversalLoader loader={(isLoading) => <InitLoader visible={isLoading} />}>
            {renderContents()}
          </UniversalLoader>
        </QueryParamProvider>
      </HelmetProvider>
    </Router>
  );
};
